/* eslint-disable @khanacademy/ts-no-error-suppressions */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {AccordionSection} from "@khanacademy/wonder-blocks-accordion";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingXSmall, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {
    markMinorConversions,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {RegionOverrideResult} from "@khan/data-region-override";
import {doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {a11yStyles, pageHeaderHeight} from "@khan/shared-styles";
import {getLoginUrl, getSignupUrl} from "@khan/urls";

import {getKaid} from "#data-access/profile-utils.ts";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import {type WithNotificationsProps} from "#notifications-package/with-notifications.tsx";
import {RegionOverrideQuery} from "#regional-standards-package/region-override-query.tsx";

import AINavItem from "../ai-nav-item.tsx";
import DonateButton from "../donate-button.tsx";
import NotificationsList from "../notifications-list.tsx";
import NotificationsSwitch from "../notifications-switch.tsx";
import type {DomainObject} from "../types.ts";
import UserSettings from "../user-settings.tsx";
import {getProfileDropdownData} from "../util.ts";

import DomainNavSection from "./domain-nav-section.tsx";

import externalLinkIcon from "@phosphor-icons/core/bold/arrow-square-out-bold.svg";

type OwnProps = WithNotificationsProps & {
    domains: Array<DomainObject>;
    onClickLink: () => void;
};

type Props = OwnProps &
    WithPrefersReducedMotionProps &
    WithUserProfileProps &
    WithMarkConversionProps &
    WithLocationProps;

type DefaultProps = {
    onClickLink: Props["onClickLink"];
};

type State = {
    showNotifications: boolean;
    donateModalOpen: boolean;
};

/**
 * A responsive navigation menu shown on small screen. Consists of the learn
 * menu and the user menu.
 */
class ResponsiveNavMenu extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        onClickLink: () => {},
    };

    state: State = {
        /**
         * Specifies whether we are displaying notifications to the user
         */
        showNotifications: false,
        donateModalOpen: false,
    };

    // A reference to the notification toggle
    notificationSwitchRef: HTMLElement | null | undefined = null;
    // Flag specifying whether we've already loaded user notifications
    hasLoadedNotifications = false;

    get isLoggedIn(): boolean {
        const {profile} = this.props;
        return Boolean(profile && !this.isPhantom);
    }

    get isPhantom(): boolean {
        const {profile} = this.props;
        return Boolean(profile && profile.get("isPhantom"));
    }

    get isTeacher(): boolean {
        const {profile} = this.props;
        return Boolean(profile && profile.get("isTeacher"));
    }

    handleNotificationsScroll: (e: React.UIEvent) => void = (e) => {
        if (
            !this.state.showNotifications ||
            !(e.target instanceof HTMLElement)
        ) {
            return;
        }

        const {scrollHeight, clientHeight, scrollTop} = e.target;
        const offset = 2;

        // Calculate when the user has scrolled to the bottom of the dropdown.
        if (scrollTop + clientHeight >= scrollHeight - offset) {
            this.props.loadNotifications();

            e.preventDefault();
        }
    };

    toggleNotifications: () => void = () => {
        const {loadNotifications} = this.props;

        // Load notifications if we haven't yet
        if (!this.hasLoadedNotifications) {
            loadNotifications();
            this.hasLoadedNotifications = true;
        }

        const showNotifications = !this.state.showNotifications;

        if (showNotifications) {
            markMinorConversions([
                {
                    id: "global_nav_open_notifications",
                    extra: {
                        view: "mobile",
                    },
                },
            ]);
        }

        this.setState({showNotifications}, () => {
            if (this.notificationSwitchRef) {
                this.notificationSwitchRef.focus();
            }

            // Invoke the clearNotifications function if we switch to the
            // notifications view
            if (showNotifications) {
                this.props.clearNotifications();
            }
        });
    };

    onUserMenuToggled: (open: boolean) => void = (open) => {
        if (open) {
            markMinorConversions([
                {
                    id: "global_nav_open_user_menu",
                    extra: {
                        view: "mobile",
                    },
                },
            ]);
        }
    };

    renderLoginSignupLinks: () => React.ReactNode = () => {
        return (
            <View style={styles.noShrink}>
                <Link
                    style={styles.linkStyle}
                    href={getLoginUrl()}
                    onClick={this.props.onClickLink}
                    safeWithNav={() =>
                        markMinorConversions([
                            {
                                id: "global_nav_login_clicked",
                            },
                        ])
                    }
                >
                    <LabelLarge>{t`Log in`}</LabelLarge>
                </Link>
                <Link
                    style={styles.linkStyle}
                    href={getSignupUrl()}
                    onClick={this.props.onClickLink}
                >
                    <LabelLarge>{t`Sign up`}</LabelLarge>
                </Link>
            </View>
        );
    };

    renderLearnMenu: (regionResult: RegionOverrideResult) => React.ReactNode = (
        regionResult,
    ) => {
        const {domains, onClickLink, prefersReducedMotion} = this.props;

        return (
            <ul className={css(styles.learnMenuList)}>
                {this.renderStandardsGuidesLink(regionResult)}
                {domains.map((domain: DomainObject, index) => (
                    <li key={index} style={styles.listReset}>
                        <DomainNavSection
                            domain={domain}
                            animated={!prefersReducedMotion}
                            onClickLink={onClickLink}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    // Renders a link to the standards alignment guides above the first domain heading.
    // Currently restricted to logged-out users and (logged-in) teachers determined to have a US region on the English KA locale site.
    // (See useRegionOverride for region identification details.)
    renderStandardsGuidesLink: (
        regionResult: RegionOverrideResult,
    ) => React.ReactNode = (regionResult) => {
        if (this.isLoggedIn && !this.isTeacher) {
            return null;
        }

        if (
            !(
                regionResult.status === "success" &&
                regionResult.regionCode.toUpperCase().startsWith("US") &&
                RequestInfo.kaLocale === "en"
            )
        ) {
            return null;
        }

        const href = "/regions/united-states";
        const translatedTitle = doNotTranslateYet`Browse By Standards`;

        return (
            <li>
                <Link
                    href={href}
                    style={styles.browseByStandardsLinkStyle}
                    target="_blank"
                >
                    <HeadingXSmall>
                        {translatedTitle}
                        <PhosphorIcon
                            aria-hidden="true"
                            icon={externalLinkIcon}
                            size="small"
                            style={styles.browseByStandardsIcon}
                        />
                    </HeadingXSmall>
                </Link>
            </li>
        );
    };

    renderUserMenu: () => React.ReactNode = () => {
        const {prefersReducedMotion, profile} = this.props;
        const profileData = getProfileDropdownData(profile);

        if (!profile || !profileData) {
            return null;
        }

        const {hasNewNotifications, onClickLink} = this.props;
        const {nickName: _, ...restProfileData} = profileData;

        return (
            <ul>
                <li>
                    <AccordionSection
                        animated={!prefersReducedMotion}
                        caretPosition="start"
                        cornerKind="square"
                        header={
                            <LabelLarge style={styles.userMenuHeaderStyle}>
                                {profile.get("nickname") || ""}
                            </LabelLarge>
                        }
                        onToggle={this.onUserMenuToggled}
                        style={styles.accordionSection}
                        tag="h4"
                    >
                        <UserSettings
                            {...restProfileData}
                            itemStyle={styles.userMenuItem}
                            signUpUrl="/signup"
                            style={styles.userMenu}
                            tabbable={true}
                            onCloseDropdown={onClickLink}
                            navigateToNotificationsSwitch={() => {}}
                        />
                    </AccordionSection>
                </li>
                <NotificationsSwitch
                    innerRef={(node) => (this.notificationSwitchRef = node)}
                    onClick={this.toggleNotifications}
                    hasNewNotifications={hasNewNotifications}
                />
            </ul>
        );
    };

    renderNotificationList: () => React.ReactNode = () => {
        const {hasNewNotifications, notifications} = this.props;

        return (
            <View
                style={styles.container}
                // @ts-expect-error - TS2769 - No overload matches this call.
                onScroll={this.handleNotificationsScroll}
            >
                <NotificationsSwitch
                    innerRef={(node) => (this.notificationSwitchRef = node)}
                    direction="left"
                    onClick={this.toggleNotifications}
                    hasNewNotifications={hasNewNotifications}
                />
                <NotificationsList
                    notifications={notifications}
                    tabbable={true}
                />
            </View>
        );
    };

    renderFooterLinks: () => React.ReactNode = () => {
        const isLoggedIn = this.isLoggedIn;
        const isPhantom = this.isPhantom;
        const {profile, publishAnalyticsEvent} = this.props;
        // Show the about link to visitors of khanacademy that have not taken
        // any learning actions.
        const showAboutLink = !isLoggedIn && !isPhantom;

        // We don't want to show the donate button on intl locales.
        const showDonateLink = RequestInfo.kaLocale === "en";

        if (!showDonateLink && !showAboutLink) {
            return null;
        }

        return (
            <React.Fragment>
                {showAboutLink && <Strut size={spacing.medium_16} />}
                <ul>
                    {showAboutLink && (
                        <li>
                            <a className={css(styles.linkStyle)} href="/about">
                                {/* Two sections, one for screens, and one for
                                screen readers */}
                                <LabelLarge aria-hidden="true">
                                    {t`About`}
                                </LabelLarge>

                                <span className={css(a11yStyles.srOnly)}>
                                    {t`About Khan Academy`}
                                </span>
                            </a>
                        </li>
                    )}

                    {showDonateLink && (
                        <li className={css(styles.donateButtonContainer)}>
                            <DonateButton
                                testId="mobile-menu-donate-link"
                                safeWithNav={() => {
                                    // Deprecated: the CEDAR event
                                    // GlobalNavBarDonateLinkClicked is intended
                                    // to replace the global_nav_donate_clicked
                                    // Bingo conversion.
                                    //
                                    // At the time of writing it's unclear
                                    // whether or not there are any existing
                                    // reports dependent on this conversion,
                                    // however. Until it can be confirmed that
                                    // the conversion is no longer in use we'll
                                    // continue reporting it.
                                    const conversionOp = markMinorConversions([
                                        {
                                            id: "global_nav_donate_clicked",
                                            // the mobile donate button is in the responsive nav menu
                                            // which is hidden/shown based off media queries
                                            extra: {
                                                view: "mobile",
                                                isLoggedIn: isLoggedIn,
                                                isPhantom: isPhantom,
                                                kaid: getKaid(profile) || "",
                                            },
                                        },
                                    ]);

                                    const analyticsOp = publishAnalyticsEvent({
                                        eventType:
                                            "GlobalNavBarDonateLinkClicked",
                                        eventSchemaVersion: 1,
                                        presentation: "NAV_MENU",
                                        currentPageURL:
                                            this.props.location.href,
                                    });

                                    return Promise.all([
                                        conversionOp,
                                        analyticsOp,
                                    ]);
                                }}
                                urlParams={{
                                    c_src2: "Webapp.responsivenav",
                                }}
                                style={styles.donateButton}
                            />
                        </li>
                    )}
                </ul>
            </React.Fragment>
        );
    };

    recordDonateButtonClicked() {
        const {profile, publishAnalyticsEvent, location} = this.props;

        // Deprecated: the CEDAR event GlobalNavBarDonateLinkClicked is
        // intended to replace the global_nav_donate_clicked Bingo
        // conversion.
        //
        // At the time of writing it's unclear whether or not there are
        // any existing reports dependent on this conversion, however.
        // Until it can be confirmed that the conversion is no longer in
        // use we'll continue reporting it.
        const conversionOp = markMinorConversions([
            {
                id: "global_nav_donate_clicked",
                // the desktop donate button is in the header
                // and is hidden/shown based off media queries
                extra: {
                    view: "mobile",
                    isLoggedIn: this.isLoggedIn,
                    isPhantom: this.isPhantom,
                    kaid: getKaid(profile),
                },
            },
        ]);

        const analyticsOp = publishAnalyticsEvent({
            eventType: "GlobalNavBarDonateLinkClicked",
            eventSchemaVersion: 1,
            presentation: "NAV_MENU",
            currentPageURL: location.href,
        });

        return Promise.all([conversionOp, analyticsOp]);
    }

    render(): React.ReactNode {
        // TODO(FEI-3212): Extract renderNotificationList into its own component
        return this.state.showNotifications ? (
            this.renderNotificationList()
        ) : (
            // RegionOverrideQuery is used to determine if the Browse By Standards link should be rendered.
            <RegionOverrideQuery>
                {(regionResult) => (
                    <View style={styles.container}>
                        {/* Registration/Signup Links */}
                        {!this.isLoggedIn && (
                            <React.Fragment>
                                {this.renderLoginSignupLinks()}
                            </React.Fragment>
                        )}

                        {/* Khanmigo Link (only WHAM) */}
                        <AINavItem
                            profile={this.props.profile}
                            profileLoading={this.props.profileLoading}
                            navItemStyles={[styles.linkStyle]}
                            isResponsiveNavMenu={true}
                            isOpen={false}
                            onToggle={() => {}}
                        />

                        {/* Learn Menu */}
                        {this.renderLearnMenu(regionResult)}

                        {/* User Menu */}
                        {this.isLoggedIn && (
                            <React.Fragment>
                                <Strut size={spacing.medium_16} />
                                {this.renderUserMenu()}
                            </React.Fragment>
                        )}

                        {/* About/Donation footer */}
                        {this.renderFooterLinks()}
                    </View>
                )}
            </RegionOverrideQuery>
        );
    }
}

const BORDER_STYLE = `1px solid ${color.offBlack16}`;

const styles = StyleSheet.create({
    container: {
        backgroundColor: color.offWhite,
        height: `calc(100vh - ${pageHeaderHeight}px)`,
        overflowY: "auto",
    },
    linkStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        borderBottom: BORDER_STYLE,
        padding: spacing.medium_16,
        boxSizing: "border-box",
        backgroundColor: color.white,
        color: color.darkBlue,
        cursor: "pointer",
        textDecoration: "none",

        ":active": {
            textDecoration: "underline",
        },

        ":hover": {
            color: color.blue,
            textDecoration: "underline",
        },
    },
    accordionSection: {
        // Remove the side borders from WB AccordionSection for consistency.
        borderLeft: "none",
        borderRight: "none",
    },
    userMenu: {
        borderTop: BORDER_STYLE,
    },
    userMenuItem: {
        padding: `${spacing.small_12}px ${spacing.xLarge_32}px`,
    },
    userMenuHeaderStyle: {
        padding: spacing.medium_16,
    },
    learnMenuList: {
        borderBottom: BORDER_STYLE,
    },
    noShrink: {
        flex: "0 0 auto",
    },
    donateButton: {
        width: "100%",
    },
    donateButtonContainer: {
        padding: spacing.medium_16,
    },
    browseByStandardsLinkStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: spacing.medium_16,
        boxSizing: "border-box",
        backgroundColor: color.white,
        color: color.blue,
        cursor: "pointer",
        textDecoration: "none",

        ":active": {
            textDecoration: "underline",
        },

        ":hover": {
            textDecoration: "underline",
        },
    },
    browseByStandardsIcon: {
        color: color.blue,
        marginLeft: spacing.xxxSmall_4,
    },
    listReset: {
        listStyleType: "none",
    },
});

type ExportProps = WithoutMarkConversion<
    WithoutUserProfile<
        WithoutPrefersReducedMotion<
            WithoutLocation<
                JSX.LibraryManagedAttributes<
                    typeof ResponsiveNavMenu,
                    React.ComponentProps<typeof ResponsiveNavMenu>
                >
            >
        >
    >
>;

export default compose(
    withUserProfile,
    withPrefersReducedMotion,
    withMarkConversion,
    withLocation,
)(ResponsiveNavMenu) as React.ComponentType<ExportProps>;
